import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cell from "./Cell";
import Walk from "./Walk";
import Detail from "./Detail";
import Assembly from "./Assembly";
import Stage from "./Stage";
import useBeat, { CAST_BEATS, STEP_BEATS, prefersReducedMotion } from "./useBeat";
import useCompact from "./useCompact";

// The shield, built in front of you.
//
// The step is in the URL and the server renders every one of these twelve
// moves on its own, so this file is an enhancement and not the feature: with
// no JavaScript the page is a shield, a form with Previous and Next, and the
// same narration. What it adds is that the figures ARRIVE — one line of dots
// at a time, in the order they were made — and that any of the fifteen can be
// asked what it is.
//
// NOTHING IS EVER HIDDEN. A figure the walk has not reached is dimmed, the one
// it just made is ringed, and the Judge is legible from the first move to the
// last. That is the Tree's rule for traversals, and it is why the reader who
// was sent a link still sees the answer.
// Deliberately unhurried. A line of a figure is one dot or two, and the whole
// claim of the art is that those four lines are all there is — which does not
// land if four of them flick past in half a second. The cast is sixteen lines
// and takes about seven seconds; a derived figure is four and takes two.
// Two paces, because the two things a reader does here are different. STEPPING
// through a move is studying it, so a line takes its time and the dots are
// watched across the stage. PLAYING is watching the whole arc, and at the
// studying pace that is nearly a minute of sitting still.
const CAST_INTERVAL = 460;
const STEP_INTERVAL = 980;
const CAST_PLAYING = 260;
const STEP_PLAYING = 430;
// The rest between one move finishing and the next beginning. Short, because
// the beats themselves are already unhurried: a full build is the cast's seven
// seconds plus eleven moves of about two and a half, and the reader can stop
// it at any point.
const PLAY_PAUSE = 450;

export default function Shield({ payload }) {
  const { figures, cells, steps, chart, lastStep } = payload;
  const [step, setStep] = useState(payload.step);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  // ARRIVING AT THE FIRST MOVE MEANS THE CAST WAS JUST MADE, so it plays
  // itself: sixteen lines of points, then the eleven figures nobody cast,
  // through to the Judge. Waiting for a second press meant the page arrived
  // finished and still, which is exactly what it looked like.
  //
  // Stop is the first control under the stage, and every later step is
  // reached by pressing something — so nothing else starts on its own.
  const [playing, setPlaying] = useState(
    () => payload.step === 0 && !prefersReducedMotion()
  );
  const compact = useCompact();
  const walking = step !== null && step !== undefined;

  const cellsByKey = useMemo(() => Object.fromEntries(cells.map((cell) => [cell.key, cell])), [cells]);

  // The cast lays down sixteen lines; every later move lays down four.
  const total = step === 0 ? CAST_BEATS : STEP_BEATS;
  const { beat, done, reduced } = useBeat(total, {
    interval: step === 0
      ? (playing ? CAST_PLAYING : CAST_INTERVAL)
      : (playing ? STEP_PLAYING : STEP_INTERVAL),
    enabled: walking,
    key: step ?? -1
  });

  const write = useCallback(
    (next) => {
      const url = new URL(window.location.href);
      if (next === null) url.searchParams.delete("step");
      else url.searchParams.set("step", String(next));
      window.history.pushState({ geomancyStep: next }, "", url);
    },
    []
  );

  const go = useCallback(
    (next) => {
      const clamped = next === null ? null : Math.min(Math.max(next, 0), lastStep);
      setStep(clamped);
      write(clamped);
    },
    [lastStep, write]
  );

  // Back walks backwards through the moves, because every move is an address.
  useEffect(() => {
    const onPop = () => {
      const value = new URL(window.location.href).searchParams.get("step");
      setPlaying(false);
      if (value === null || value === "") setStep(null);
      else {
        const parsed = Number.parseInt(value, 10);
        setStep(Number.isNaN(parsed) ? null : Math.min(Math.max(parsed, 0), lastStep));
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [lastStep]);

  // Play waits for the move to finish arriving before starting the next one,
  // so the sequence is paced by the drawing rather than racing it. It STOPS at
  // the end; it does not begin again from the cast.
  useEffect(() => {
    if (!playing || !walking) return undefined;
    if (step >= lastStep) {
      setPlaying(false);
      return undefined;
    }
    if (!done) return undefined;

    const timer = window.setTimeout(() => go(step + 1), reduced ? PLAY_PAUSE : PLAY_PAUSE);
    return () => window.clearTimeout(timer);
  }, [playing, walking, done, step, lastStep, go, reduced]);

  const stateFor = (cell) => {
    if (!walking) return null;
    if (cell.step < step) return "past";
    if (cell.step === step) return "current";
    return "future";
  };

  // WHAT THE CURRENT BEAT MEANS, once, for every cell on the shield.
  //
  // `beat` counts lines completed, so at beat b lines 0..b-1 have landed and
  // line b is the one arriving. The cast is sixteen of those — four Mothers of
  // four lines each, in the order they were made — and every later move is
  // four.
  //
  // A move also READS lines off figures already on the shield, and those are
  // lit where they actually stand: a Daughter's third line is some Mother's
  // line, and you should be able to watch it leave. The source is found by
  // CELL and never by figure, because one figure can stand in three places on
  // a shield and a slug does not say which of them was read.
  const moment = useMemo(() => {
    const landing = {};
    const source = {};
    if (!walking) return { landing, source, landed: null, active: null };

    const current = steps[step];
    const arriving = beat < total ? beat : null;

    if (step === 0) {
      [0, 1, 2, 3].forEach((index) => {
        const local = beat - index * 4;
        if (local >= 0 && local < 4) landing[`mother-${index + 1}`] = local;
      });
      return { landing, source, landed: null, active: null };
    }

    const made = current.made[0];
    const cell = cells.find((one) => one.step === step && one.figure === made);
    if (cell && arriving !== null) landing[cell.key] = arriving;

    if (arriving !== null) {
      if (current.kind === "transpose") {
        // A Daughter's nth line is the nth MOTHER's line — the one whose
        // position in that Mother matches this Daughter's own number.
        const which = current.sources[arriving];
        if (which) source[which.cell] = which.line;
      } else {
        current.sources.forEach((one) => {
          source[one.cell] = arriving;
        });
      }
    }

    return { landing, source, landed: beat, active: arriving };
  }, [walking, step, beat, total, steps, cells]);

  // How many of this figure's four lines have landed. Only the move being made
  // is drawn arriving; everything already on the shield is simply there.
  const revealedFor = (cell) => {
    if (!walking || cell.step !== step) return null;
    if (step === 0) {
      const index = Number(cell.key.split("-")[1]) - 1;
      return Math.min(Math.max(beat - index * 4, 0), 4);
    }
    return Math.min(beat, 4);
  };

  const selectedCell = selected ? cellsByKey[selected] : null;
  const selectedStep = selectedCell ? steps[selectedCell.step] : null;

  const tier = (keys, size) =>
    keys.map((key) => {
      const cell = cellsByKey[key];
      const state = stateFor(cell);
      return (
        <Cell
          key={key}
          cell={cell}
          figure={figures[cell.figure]}
          size={size}
          state={state}
          selected={selected === key}
          arriving={state === "current" && !reduced && !done}
          revealed={revealedFor(cell)}
          landingLine={moment.landing[key] ?? null}
          sourceLine={moment.source[key] ?? null}
          beat={beat}
          showCard={hovered === key}
          onSelect={(which) => setSelected((was) => (was === which ? null : which))}
          onHover={setHovered}
        />
      );
    });

  const mothers = ["mother-1", "mother-2", "mother-3", "mother-4"];
  const daughters = ["daughter-1", "daughter-2", "daughter-3", "daughter-4"];
  const nieces = ["niece-1", "niece-2", "niece-3", "niece-4"];

  return (
    <div className="geo-island">
      <div
        className={`shield${walking ? " shield--walking" : ""}`}
        data-testid="shield"
        data-step={walking ? step : ""}
        data-last-step={lastStep}
      >
        <div className="shield__tier shield__tier--top">
          <h3 className="shield__label">Cast — four Mothers, then their Daughters</h3>
          <ol className="shield__row shield__row--rtl">
            {tier(mothers, 44)}
            {tier(daughters, 44)}
          </ol>
        </div>

        <div className="shield__tier">
          <h3 className="shield__label">Nieces — each the sum of the two above it</h3>
          <ol className="shield__row shield__row--rtl">{tier(nieces, 40)}</ol>
        </div>

        <div className="shield__tier">
          <h3 className="shield__label">Witnesses — the Coadjutrices, or Testes</h3>
          <ol className="shield__row shield__row--rtl">
            {tier(["right-witness", "left-witness"], 40)}
          </ol>
        </div>

        <div className="shield__tier">
          <h3 className="shield__label">Judge — the Index of the whole figure</h3>
          <ol className="shield__row">{tier(["judge"], 52)}</ol>
        </div>

      </div>

      {!walking ? (
        <p className="geo-walk__start">
          <button
            type="button"
            className="geo-walk__start-link"
            data-testid="geomancy-walk-start"
            onClick={() => {
              setPlaying(false);
              go(0);
            }}
          >
            Build it step by step
          </button>
        </p>
      ) : null}

      <Detail
        cell={selectedCell}
        figure={selectedCell ? figures[selectedCell.figure] : null}
        figures={figures}
        addition={selectedStep ? selectedStep.addition : null}
      />

      {walking ? (
        <Walk
          steps={steps}
          figures={figures}
          step={step}
          playing={playing}
          onStep={(next) => {
            setPlaying(false);
            go(next);
          }}
          stage={
            <Stage
              step={steps[step]}
              figures={figures}
              beat={beat}
              total={total}
              reduced={reduced}
              compact={compact}
            />
          }
          assembly={
            <Assembly
              step={steps[step]}
              figures={figures}
              landed={moment.landed}
              active={moment.active}
            />
          }
          onPlay={() => setPlaying((was) => !was)}
          onFinish={() => {
            setPlaying(false);
            go(null);
          }}
        />
      ) : null}

      <p className="geo-island__chart visually-hidden" data-chart={chart.param} />
    </div>
  );
}
